import React from 'react';
import { Box, Typography, Tooltip, alpha } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  Description as NoteIcon,
  FolderShared as EhrIcon,
  Biotech as LabIcon,
  ImageSearch as ImagingIcon,
  Medication as MedicationIcon,
  Assignment as AssessmentIcon,
  ReceiptLong as ClaimsIcon,
  MonitorHeart as VitalsIcon,
} from '@mui/icons-material';
import type { ClinicalReference, ClinicalReferenceType } from '~/types/clinical-reference';

interface ClinicalNarrativeProps {
  // Narrative text, optionally containing inline `[[n]]` citation markers.
  text: string;
  // References keyed by the number used in the markers.
  references?: ClinicalReference[];
  sx?: SxProps<Theme>;
}

const REFERENCE_ICON: Record<ClinicalReferenceType, React.ReactNode> = {
  ehr: <EhrIcon fontSize="inherit" />,
  note: <NoteIcon fontSize="inherit" />,
  lab: <LabIcon fontSize="inherit" />,
  imaging: <ImagingIcon fontSize="inherit" />,
  medication: <MedicationIcon fontSize="inherit" />,
  assessment: <AssessmentIcon fontSize="inherit" />,
  claims: <ClaimsIcon fontSize="inherit" />,
  vitals: <VitalsIcon fontSize="inherit" />,
};

// Splits on the `[[n]]` markers while keeping the captured markers in the array.
const MARKER_SPLIT = /(\[\[\d+\]\])/g;
const MARKER_MATCH = /^\[\[(\d+)\]\]$/;

const ReferencePill: React.FC<{ reference: ClinicalReference }> = ({ reference }) => {
  const icon = REFERENCE_ICON[reference.sourceType ?? 'note'];

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ py: 0.5, maxWidth: 260 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ fontSize: '1rem', display: 'flex', color: 'primary.light' }}>{icon}</Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {reference.source}
            </Typography>
          </Box>
          {reference.detail && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'grey.300' }}>
              {reference.detail}
            </Typography>
          )}
          {reference.snippet && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, fontStyle: 'italic', lineHeight: 1.4, color: 'grey.100' }}
            >
              “{reference.snippet}”
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        component="span"
        role="button"
        tabIndex={0}
        aria-label={`Source ${reference.id}: ${reference.source}`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 16,
          height: 16,
          px: 0.4,
          ml: 0.3,
          verticalAlign: 'super',
          fontSize: '0.65rem',
          fontWeight: 700,
          lineHeight: 1,
          borderRadius: '8px',
          cursor: 'pointer',
          userSelect: 'none',
          color: 'primary.main',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
          transition: 'background-color 120ms ease, color 120ms ease',
          '&:hover, &:focus-visible': {
            color: 'common.white',
            bgcolor: 'primary.main',
            borderColor: 'primary.main',
            outline: 'none',
          },
        }}
      >
        {reference.id}
      </Box>
    </Tooltip>
  );
};

/**
 * Renders an AI-generated clinical narrative, converting inline `[[n]]` markers
 * into hover-able citation pills backed by `references`. Markers with no
 * matching reference are dropped so raw tokens never leak into the UI.
 */
const ClinicalNarrative: React.FC<ClinicalNarrativeProps> = ({ text, references, sx }) => {
  const byId = React.useMemo(() => {
    const map = new Map<number, ClinicalReference>();
    references?.forEach((ref) => map.set(ref.id, ref));
    return map;
  }, [references]);

  const segments = React.useMemo(() => text.split(MARKER_SPLIT), [text]);

  return (
    <Typography
      variant="body1"
      component="div"
      sx={{ whiteSpace: 'pre-line', lineHeight: 1.7, ...sx }}
    >
      {segments.map((segment, index) => {
        const match = segment.match(MARKER_MATCH);
        if (match) {
          const ref = byId.get(Number(match[1]));
          return ref ? <ReferencePill key={index} reference={ref} /> : null;
        }
        return <React.Fragment key={index}>{segment}</React.Fragment>;
      })}
    </Typography>
  );
};

export default ClinicalNarrative;
